import { useId, useState, type CSSProperties } from 'react';

import type { ClientDashboardDto, Lane } from '@botguide/shared';
import { LANES, LANE_LABELS } from '@botguide/shared';

import { BlockedBadge, Icon, LaneHeading, ProgressBar, laneColour } from '@/components';
import { cn } from '@/lib/cn';
import { formatCount } from '@/lib/format';

/*
 * The lanes, in the client's language.
 *
 * Nothing in this file renders a person, and nothing it receives carries one:
 * `ClientDashboardDto` has no assignee, no author and no reviewer anywhere in
 * its shape. That is the API's guarantee and this component must not reach
 * around it for a name from another query.
 */

type DashboardApplication = ClientDashboardDto['applications'][number];

/**
 * What a lane means to somebody paying for the work, rather than to us.
 *
 * An exhaustive `Record<Lane, string>`, so adding a lane to the shared enums
 * fails the typecheck here instead of shipping an unexplained column.
 */
const LANE_MEANING: Record<Lane, string> = {
  backlog: 'Agreed, not yet scheduled',
  next_up: 'Scheduled to start next',
  in_progress: 'Being built now',
  completed: 'Delivered and in use',
};

export interface LaneSummaryProps {
  lanes: DashboardApplication['lanes'];
  features: DashboardApplication['features'];
  className?: string;
}

/**
 * Four columns: what is waiting, what is next, what is being built, what is
 * done - with the features inside each one.
 *
 * The seven SDLC stages are deliberately absent. A feature's rolled-up lane is
 * the honest answer to "where is this up to"; a stage is an internal step in
 * how we get there, and `ClientDashboardDto` does not carry one.
 */
export function LaneSummary({ lanes, features, className }: LaneSummaryProps) {
  const byLane = new Map(lanes.map((entry) => [entry.lane, entry]));
  const featuresByLane = new Map<Lane, DashboardApplication['features']>();
  for (const feature of features) {
    const list = featuresByLane.get(feature.rollup.lane) ?? [];
    list.push(feature);
    featuresByLane.set(feature.rollup.lane, list);
  }

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {LANES.map((lane) => {
        const counts = byLane.get(lane);
        const laneFeatures = featuresByLane.get(lane) ?? [];

        return (
          <section key={lane} className="min-w-0">
            <LaneHeading lane={lane} count={counts?.featureCount ?? laneFeatures.length} />
            <p className="-mt-1 mb-3 text-[0.75rem] text-subtle">
              {LANE_MEANING[lane]}
              {counts && counts.taskCount > 0 ? ` · ${formatCount(counts.taskCount, 'item')}` : ''}
            </p>

            {laneFeatures.length === 0 ? (
              <p
                className="rounded-md border border-dashed border-line px-3 py-4 text-center text-[0.8125rem] text-subtle"
                style={{ borderColor: `color-mix(in srgb, ${laneColour(lane)} 22%, transparent)` }}
              >
                Nothing in {LANE_LABELS[lane].toLowerCase()}
              </p>
            ) : (
              <FeatureList lane={lane} features={laneFeatures} />
            )}
          </section>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * How many features a lane shows before it asks.
 *
 * Three, because a lane is one column of four and the card below it still has
 * to be reachable. The 2026-10-15 delivery backfill put dozens of features in
 * Completed for a single application; uncapped, one lane buried the rest of
 * the page.
 */
const VISIBLE_FEATURES = 3;

/**
 * A lane's features, capped, with the remainder behind a button.
 *
 * The hidden rows are not rendered and hidden with CSS - they are not
 * rendered at all. That is the point of the cap, and it is also why
 * `e2e/smoke.spec.ts` expands every lane before asserting that no BotGuide
 * person is named on the dashboard: with the rows absent, that assertion
 * would otherwise pass for the wrong reason.
 *
 * State lives here rather than in `LaneSummary` so each lane of each
 * application expands on its own. One shared flag would open Completed on
 * every card at once.
 */
function FeatureList({
  lane,
  features,
}: {
  lane: Lane;
  features: DashboardApplication['features'];
}) {
  const [expanded, setExpanded] = useState(false);
  /*
   * `aria-controls` needs a real id, and this component renders once per lane
   * per application - four lanes times every application a client can see.
   * `useId` is the only source of one that survives that without collisions.
   */
  const listId = useId();

  const hidden = features.length - VISIBLE_FEATURES;
  const shown = expanded ? features : features.slice(0, VISIBLE_FEATURES);

  return (
    <>
      <ul id={listId} className="flex flex-col gap-2">
        {shown.map((feature) => (
          <li key={feature.id}>
            <FeatureRow feature={feature} lane={lane} />
          </li>
        ))}
      </ul>

      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={listId}
          className="mt-2 w-full rounded-md border border-dashed border-line px-3 py-2 text-[0.75rem] font-medium text-muted transition-colors hover:border-line-strong hover:text-ink"
        >
          {/*
            The count is the content. "Show more" leaves a client guessing
            whether two features are hidden or forty, and the lane heading's
            own total is several lines away by the time they reach this.
          */}
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </button>
      ) : null}
    </>
  );
}

/**
 * One feature. The name is ours; `clientOutcome` is the sentence written for
 * the client, so it is the one that gets the room when it exists.
 */
function FeatureRow({
  feature,
  lane,
}: {
  feature: DashboardApplication['features'][number];
  lane: Lane;
}) {
  const { rollup } = feature;
  const blocked = rollup.blockedCount > 0;

  return (
    <article
      className="rule-lead rounded-md border border-line bg-surface px-3 py-2.5 transition-colors duration-200 hover:border-line-strong"
      style={{ '--rule-colour': laneColour(lane) } as CSSProperties}
    >
      <h4 className="text-[0.875rem] font-semibold leading-snug">{feature.name}</h4>

      {feature.clientOutcome ? (
        <p className="mt-1 text-[0.8125rem] leading-relaxed text-muted">{feature.clientOutcome}</p>
      ) : null}

      {lane === 'completed' ? (
        <p className="mt-2 inline-flex items-center gap-1.5 text-[0.75rem] font-medium text-ok-ink">
          <Icon name="check" size={13} />
          All {formatCount(rollup.taskCount, 'item')} delivered
        </p>
      ) : lane === 'in_progress' ? (
        <ProgressBar
          className="mt-2.5"
          size="sm"
          value={rollup.progress}
          showValue
          label={`${rollup.completedCount} of ${rollup.taskCount} done`}
          tone={laneColour('in_progress')}
          blockedFraction={rollup.taskCount === 0 ? 0 : rollup.blockedCount / rollup.taskCount}
        />
      ) : (
        <p className="mt-2 font-mono text-[0.75rem] text-subtle tnum">
          {formatCount(rollup.taskCount, 'item')}
          {rollup.totalPoints > 0 ? ` · ${rollup.totalPoints}p` : ''}
        </p>
      )}

      {blocked ? (
        <BlockedBadge
          className="mt-2"
          reason={`${rollup.blockedCount} of ${rollup.taskCount} items`}
        />
      ) : null}
    </article>
  );
}
